"use client";

import { UnstyledButton } from "@opensea/ui-kit";
import { createContext, useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { getBytesFromMegabytes, readFileAsDataURL } from "@/lib/upload";
import { useCollectionMetadataMediaUpload } from "./use-collection-metadata-media-upload";
const MediaUploadContext = createContext(undefined);
export function useMediaUpload() {
  const context = useContext(MediaUploadContext);
  if (!context) {
    throw new Error("useMediaUpload must be used within a MediaUpload component");
  }
  return context;
}
const MAX_SIZE = getBytesFromMegabytes(100);
export function MediaUpload({
  slug,
  supportedFileTypes = ["jpg", "png", "svg", "gif", "webp"],
  children,
  defaultMediaUrl,
  uploadAttribute,
  section = "OVERVIEW",
  onError,
  onSuccess,
  className
}) {
  const {
    upload,
    isUploading
  } = useCollectionMetadataMediaUpload(slug, uploadAttribute, section);
  const [url, setUrl] = useState(defaultMediaUrl ?? undefined);
  const [acceptedFile, setAcceptedFile] = useState(undefined);

  // Cleanup data URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (url?.startsWith("data:")) {
        URL.revokeObjectURL(url);
      }
    };
  }, [url]);
  useEffect(() => {
    // Only update from defaultMediaUrl if no file has been uploaded
    if (defaultMediaUrl && !acceptedFile) {
      setUrl(defaultMediaUrl);
    }
  }, [defaultMediaUrl, acceptedFile]);
  const {
    getRootProps,
    getInputProps
  } = useDropzone({
    disabled: isUploading,
    accept: supportedFileTypes.reduce((acc, fileExtension) => {
      switch (fileExtension) {
        case "jpg":
        case "jpeg":
          acc["image/jpeg"] = [".jpg", ".jpeg"];
          break;
        case "png":
          acc["image/png"] = [".png"];
          break;
        case "svg":
          acc["image/svg+xml"] = [".svg"];
          break;
        case "gif":
          acc["image/gif"] = [".gif"];
          break;
        case "webp":
          acc["image/webp"] = [".webp"];
          break;
        case "mp4":
          acc["video/mp4"] = [".mp4"];
          break;
        default:
          console.warn(`Unsupported file type: ${fileExtension}`);
      }
      return acc;
    }, {}),
    maxFiles: 1,
    maxSize: MAX_SIZE,
    onDrop: async acceptedFiles => {
      try {
        setAcceptedFile(acceptedFiles[0]);
        const tokens = await upload(acceptedFiles[0]);
        if (tokens?.length) {
          const url_0 = await readFileAsDataURL(acceptedFiles[0]);
          setUrl(url_0);
          onSuccess?.({
            token: tokens[0],
            url: url_0,
            file: acceptedFiles[0]
          });
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        onError?.(error);
      }
    }
  });
  const value = {
    isUploading,
    url,
    file: acceptedFile
  };
  return <MediaUploadContext.Provider value={value}>
      <UnstyledButton {...getRootProps()} aria-label="Upload media file" className={className} role="button" tabIndex={0}>
        {children}
        <input {...getInputProps()} aria-label="File upload input" />
      </UnstyledButton>
    </MediaUploadContext.Provider>;
}